import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Folder as FolderIcon, Images, Upload } from 'lucide-react';
import type { Asset, MediaQuery, ReferenceRoleId } from '@mg/shared';
import { MEDIA_PICKER_PAGE_SIZE, isGenerationReferenceMime } from '@mg/shared';
import { actionErrorText, api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useTenantKey } from '../../lib/useTenantKey';
import { Button } from '../../components/ui/Button';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { MediaThumb } from './MediaThumb';
import { MediaSearchBox } from './MediaSearchBox';
import { LoadMoreButton } from './LoadMoreButton';
import { useMediaPages } from './useMediaPages';
import type { MediaReference } from './MediaReferenceCard';

/**
 * Raster images only, and only finished ones.
 *
 * `type: 'image'` in the query is not enough: `image/svg+xml` has the `image/` prefix, so SVG
 * (Figma templates) passed straight through, and no image model can read one. PowerPoint (Canva
 * templates) is excluded by the same allowlist rather than by relying on the type filter. Both get
 * their own generator later — they are simply not prompt references.
 */
function isUsableReference(asset: Asset): boolean {
  return asset.status === 'ready' && isGenerationReferenceMime(asset.mimeType);
}

/**
 * Library browser for picking generation references.
 *
 * Covers the PREVIEW PANE only, on an opaque background — the form stays visible and usable, so
 * the prompt you are writing is still in front of you while you choose the images it refers to.
 * Selections persist across folder switches and share one cap with uploads, because the two are a
 * single ordered list from the model's point of view.
 */
export function MediaLibraryOverlay({
  selected,
  max,
  onChange,
  onClose,
  onUpload,
  role,
}: {
  selected: MediaReference[];
  max: number;
  onChange: (next: MediaReference[]) => void;
  onClose: () => void;
  onUpload: () => void;
  /**
   * Fill ONE named slot rather than adding to a list.
   *
   * Picking is single-shot here: a slot holds one image, so a choice replaces whatever was in it
   * and closes. Leaving multi-select on would let a user tick three images for a slot that can only
   * hold one, and then silently drop two.
   */
  role?: ReferenceRoleId;
}) {
  const tenantKey = useTenantKey();
  const [folderId, setFolderId] = useState<string | null>(null);
  /** The search in force, trimmed; it narrows the open folder. */
  const [search, setSearch] = useState('');

  const folders = useQuery({
    queryKey: ['asset-folders', tenantKey],
    queryFn: ({ signal }) => api.listAssetFolders(signal),
    enabled: tenantKey !== '',
  });

  const query = useMemo<MediaQuery>(
    () => ({ type: 'image', folderId: folderId ?? undefined, ...(search ? { q: search } : {}) }),
    [folderId, search],
  );
  // A page at a time; when a page holds few usable files, the hook reads on until it has a page's worth.
  const media = useMediaPages(query, MEDIA_PICKER_PAGE_SIZE, { visible: isUsableReference });

  const loaded = media.assets;
  const assets = loaded.filter(isUsableReference);
  const chosen = new Set(selected.map((r) => r.assetId).filter(Boolean) as string[]);
  const atLimit = selected.length >= max;

  const toggle = (asset: Asset) => {
    if (role) {
      onChange([
        ...selected.filter((r) => r.role !== role),
        { assetId: asset.id, name: asset.name ?? 'Image', url: asset.thumbUrl ?? asset.blobUrl, role },
      ]);
      onClose();
      return;
    }
    if (chosen.has(asset.id)) {
      onChange(selected.filter((r) => r.assetId !== asset.id));
      return;
    }
    if (atLimit) return;
    onChange([
      ...selected,
      { assetId: asset.id, name: asset.name ?? 'Image', url: asset.thumbUrl ?? asset.blobUrl },
    ]);
  };

  return (
    // Opaque, and absolutely positioned over the preview column only.
    <div className="absolute inset-0 z-20 flex flex-col bg-card">
      <div className="flex shrink-0 items-center gap-2 border-b border-line-soft px-5 py-3">
        <Images className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
          {role ? `Choose the ${role} image` : 'Media Library'}
        </h3>
        <MediaSearchBox value={search} onSearch={setSearch} className="w-48 shrink-0" />
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Folder rail — scrolls independently of the grid. */}
        <nav
          aria-label="Folders"
          className="min-h-0 w-40 shrink-0 overflow-y-auto border-r border-line-soft py-2"
        >
          <FolderRow
            label="All media"
            icon={<Images className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />}
            active={folderId === null}
            onClick={() => setFolderId(null)}
          />
          {folders.isError && (
            <p className="px-3 py-1.5 text-[0.6875rem] leading-tight text-red-600 dark:text-error-dark">
              Folders didn&apos;t load.
            </p>
          )}
          {(folders.data ?? []).map((folder) => (
            <FolderRow
              key={folder.id}
              label={folder.name}
              count={folder.assetCount}
              icon={<FolderIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />}
              active={folderId === folder.id}
              onClick={() => setFolderId(folder.id)}
            />
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {/*
            Four distinct states, because collapsing them lies to the user.

            A FAILED request used to render as "No usable images here — references must be JPG or
            PNG", which says the library is the problem when the request never landed. And a folder
            holding only SVGs is a different situation from an empty folder: one needs a different
            format, the other needs a different folder. Saying so is the difference between a user
            fixing it and a user filing a bug.
          */}
          {media.isPending || (assets.length === 0 && media.isFetchingNextPage) ? (
            <LoadingState label="Loading media…" />
          ) : media.isError ? (
            <EmptyState
              title="Couldn't load your media"
              description={actionErrorText(media.error, 'Please try again in a moment.')}
            />
          ) : assets.length === 0 ? (
            <>
              {search ? (
                <EmptyState
                  title="No media matches your search"
                  description="Try a different word, or clear the search."
                />
              ) : (
                <EmptyState
                  title={loaded.length ? 'Nothing usable in this folder' : 'No images here'}
                  description={
                    loaded.length
                      ? `This folder has ${media.total} file${media.total === 1 ? '' : 's'}, but references must be JPG or PNG.`
                      : 'Upload one, or pick another folder.'
                  }
                />
              )}
              {/* The automatic reads stop after a few pages; more may still hold a usable image. */}
              {media.hasMore && (
                <LoadMoreButton
                  size="sm"
                  loading={media.isFetchingNextPage}
                  failed={media.loadMoreFailed}
                  onClick={media.loadMore}
                />
              )}
            </>
          ) : (
            <ul className="grid grid-cols-2 gap-2">
              {assets.map((asset) => {
                const isChosen = chosen.has(asset.id);
                // Once full, unchosen tiles are inert — but chosen ones must stay clickable so
                // the user can swap a selection without clearing everything first.
                // In slot mode a pick REPLACES, so no tile is ever inert.
                const inert = !role && atLimit && !isChosen;
                return (
                  <li key={asset.id}>
                    <button
                      type="button"
                      onClick={() => toggle(asset)}
                      disabled={inert}
                      aria-pressed={isChosen}
                      title={inert ? `Maximum ${max} references` : (asset.name ?? undefined)}
                      className={cn(
                        'focus-ring relative block aspect-square w-full overflow-hidden rounded-lg border',
                        isChosen
                          ? 'border-action ring-2 ring-action/20'
                          : inert
                            ? 'cursor-not-allowed border-line opacity-40'
                            : 'border-line hover:border-ink-ghost',
                      )}
                    >
                      <MediaThumb asset={asset} className="h-full w-full" />
                      {isChosen && (
                        <span className="absolute right-1.5 top-1.5 rounded-full bg-[#26332F] p-1 text-white">
                          <Check className="h-3 w-3" aria-hidden />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {assets.length > 0 && media.hasMore && !media.isError && (
            <div className="pt-3">
              <LoadMoreButton
                size="sm"
                loading={media.isFetchingNextPage}
                failed={media.loadMoreFailed}
                onClick={media.loadMore}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 border-t border-line-soft px-5 py-3">
        <span className="text-xs text-ink-muted">
          {role ? 'Pick one image for this slot' : `${selected.length} of ${max} selected`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onUpload}>
            <Upload className="h-4 w-4" aria-hidden />
            Upload
          </Button>
          <Button size="sm" onClick={onClose}>
            Close Media Library
          </Button>
        </div>
      </div>
    </div>
  );
}

function FolderRow({
  label,
  count,
  icon,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'focus-ring flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
        active ? 'bg-tint font-medium text-tint-fg' : 'text-ink-strong hover:bg-hover',
      )}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {count != null && <span className="shrink-0 text-xs text-ink-faint">{count}</span>}
    </button>
  );
}
